import React, { useMemo, useState } from 'react';

import { COUNT_MODE_FTE } from 'Components/people/peopleControlStore';
import { POSITION_TYPE_COLOURS, POSITION_TYPE_ORDER, positionTypeLabel } from 'Components/people/positionTypes';
import Chart from 'react-apexcharts';

import { formatSeparateByThousands, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import SegmentedControl from 'CommonComponents/SegmentedControl';

/**
 * The headcount ramp: quarterly buckets across the full project life, stacked by
 * position type, with a cumulative-cost line overlaid on a second axis.
 *
 * Two lenses only — by type, and the payload's `second_lens` (by cost centre at
 * department level, by cohort at cost centre level). There is deliberately no
 * by-commitment lens.
 *
 * This is a sibling of `Components/charts/TotalheadcountChart` rather than an
 * extension of it: that chart hardcodes the ApexCharts id `total-headcount` and
 * drives itself through `ApexCharts.exec`, carries its own react-tabs
 * Chart/Filter pair with Bootstrap-4 markup and jQuery month pickers, and reads a
 * `[{name, totals: [{date, cumulative}]}]` contract. Nothing above survives a
 * stacked-by-type, dual-axis, second-lens rewrite, and a second instance of it on
 * one page would collide on that chart id.
 */

const LENS_TYPE = 'type';
const LENS_SECOND = 'second';

// Second-lens series palette: the blue and charcoal ramps from
// tailwind.config.js, plus green. No red — that stays reserved for alerts.
const SECOND_LENS_PALETTE = ['#055DF5', '#4485F8', '#82AEFA', '#C1D7FD', '#1B1C1D', '#545556', '#8D8E8E', '#00DD8D', '#40E6AA'];

const COST_LINE_COLOUR = '#1B1C1D';

const formatCount = (value) => formatSeparateByThousands(value);
const formatMoney = (value) => formatToFixedMoneyWithLocale(value);

function secondLensLabel(mode) {
    switch (mode) {
        case 'costcentre':
            return $t('By cost centre');
        case 'cohort':
            return $t('By cohort');
        case 'department':
            return 'By department';
        default:
            return $t('By child');
    }
}

/**
 * Stacked count series.
 *
 * In FTE mode the payload carries one `fte` figure per bucket and no per-type FTE
 * breakdown, so the stack collapses to a single series. Showing the headcount
 * stack while the rail says FTE would be worse than showing one honest bar.
 */
function countSeries(buckets, countMode) {
    if (countMode === COUNT_MODE_FTE) {
        return [
            {
                name: $t('FTE'),
                type: 'column',
                data: buckets.map((bucket) => bucket.fte ?? 0),
            },
        ];
    }

    return POSITION_TYPE_ORDER.map((type) => ({
        name: positionTypeLabel(type),
        type: 'column',
        data: buckets.map((bucket) => bucket.by_type?.[type] ?? 0),
    }));
}

function secondLensSeries(secondLens) {
    return (secondLens?.series || []).map((entry) => ({
        name: entry.label,
        type: 'column',
        data: entry.buckets || [],
    }));
}

function buildChart({ buckets, secondLens, lens, countMode }) {
    const labels = buckets.map((bucket) => bucket.label);

    const series = lens === LENS_SECOND ? secondLensSeries(secondLens) : countSeries(buckets, countMode);

    const colours =
        lens === LENS_SECOND
            ? series.map((_entry, index) => SECOND_LENS_PALETTE[index % SECOND_LENS_PALETTE.length])
            : countMode === COUNT_MODE_FTE
              ? [POSITION_TYPE_COLOURS.P]
              : POSITION_TYPE_ORDER.map((type) => POSITION_TYPE_COLOURS[type]);

    // `cumulative_cost` is omitted, not nulled, for a user without the pay-rate
    // permission. `key in obj` keeps a genuine cumulative cost of 0 on the chart.
    const hasCost = buckets.length > 0 && buckets.every((bucket) => 'cumulative_cost' in bucket);

    const countLabel = countMode === COUNT_MODE_FTE ? $t('FTE') : $t('Headcount');

    const chartSeries = hasCost
        ? [
              ...series,
              {
                  name: $t('Cumulative cost'),
                  type: 'line',
                  data: buckets.map((bucket) => bucket.cumulative_cost),
              },
          ]
        : series;

    const yaxis = [
        {
            seriesName: series.length ? series[0].name : countLabel,
            title: { text: countLabel, style: { color: '#8D8E8E', fontWeight: 500 } },
            labels: { formatter: formatCount },
        },
        // Every stacked column shares the first axis; ApexCharts needs one entry
        // per series, and repeating `seriesName` is how sibling series are pinned
        // to the same scale.
        ...series.slice(1).map(() => ({
            seriesName: series[0].name,
            show: false,
            labels: { formatter: formatCount },
        })),
    ];

    if (hasCost) {
        yaxis.push({
            seriesName: $t('Cumulative cost'),
            opposite: true,
            title: { text: $t('Cumulative cost'), style: { color: '#8D8E8E', fontWeight: 500 } },
            labels: { formatter: formatMoney },
        });
    }

    return {
        series: chartSeries,
        options: {
            chart: {
                fontFamily: 'var(--font-sans)',
                type: 'line',
                width: '100%',
                stacked: true,
                // The cost overlay is a line on its own axis; stacking it onto the
                // column total would make it read as another cohort.
                stackOnlyBar: true,
                toolbar: { show: false },
                zoom: { enabled: false },
            },
            colors: hasCost ? [...colours, COST_LINE_COLOUR] : colours,
            stroke: {
                // Index-aligned with `chartSeries`: the columns get no stroke, the
                // overlay gets a 3px line.
                width: hasCost ? [...series.map(() => 0), 3] : series.map(() => 0),
                curve: 'smooth',
            },
            plotOptions: {
                bar: { columnWidth: '60%', borderRadius: 2 },
            },
            dataLabels: { enabled: false },
            legend: { show: true, position: 'bottom', horizontalAlign: 'left' },
            xaxis: {
                categories: labels,
                tickPlacement: 'on',
            },
            yaxis,
            tooltip: {
                shared: true,
                intersect: false,
                y: {
                    formatter: (value, { seriesIndex }) => (hasCost && seriesIndex === series.length ? formatMoney(value) : formatCount(value)),
                },
            },
        },
    };
}

/**
 * @param {Object} ramp      - the payload's `ramp` block
 * @param {string} countMode - 'headcount' | 'fte', from the control rail
 */
export default function PeopleHeadcountRamp({ ramp, countMode }) {
    const [lens, setLens] = useState(LENS_TYPE);

    const buckets = ramp?.buckets || [];
    const secondLens = ramp?.second_lens;
    const hasSecondLens = (secondLens?.series || []).length > 0;

    const { options, series } = useMemo(
        () => buildChart({ buckets, secondLens, lens: hasSecondLens ? lens : LENS_TYPE, countMode }),
        [ramp, lens, countMode, hasSecondLens],
    );

    if (buckets.length === 0) {
        return <p className="tw-text-sm tw-text-charcoal-50">{$t('No positions fall inside the project life yet.')}</p>;
    }

    return (
        <div>
            {hasSecondLens && (
                <div className="tw-flex tw-justify-end tw-mb-3">
                    <SegmentedControl
                        variant="track"
                        options={[
                            { value: LENS_TYPE, label: $t('By type') },
                            { value: LENS_SECOND, label: secondLensLabel(secondLens.mode) },
                        ]}
                        value={lens}
                        onChange={setLens}
                    />
                </div>
            )}
            <Chart options={options} series={series} type="line" height={340} />
        </div>
    );
}
